import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useLocation, useParams } from "react-router";
import { createLogger } from "@/debug/DebugingLogger";
const log = createLogger("NavigationProvider");

const PAGE_PATH = {
  "/": "Overview",
  "/overview": "Overview",
  "/datasets": "Datasets",
  "/connections": "Connections",
  "/teams": "Teams",
  "/shared": "SharedWithMe",
  "/trash": "Trash",
  "/access": "Access",
  "/admin": "AdminInvitations",
  "/admin/invitations": "AdminInvitations",
  "/admin/accounts": "AdminAccounts",
  "/admin/removed-accounts": "AdminRemovedAccounts",
  "/admin/service-accounts": "AdminServiceAccounts",
  "/admin/data-lifecycle": "AdminDataLifecycle",
  "/admin/audit": "AdminAudit",
} as const;

const PAGE_ROUTES: Record<string, string> = {
  Overview: "/overview",
  SharedWithMe: "/shared",
  Datasets: "/datasets",
  Connections: "/connections",
  Teams: "/teams",
  Trash: "/trash",
  Access: "/access",
  Admin: "/admin",
  AdminInvitations: "/admin/invitations",
  AdminAccounts: "/admin/accounts",
  AdminRemovedAccounts: "/admin/removed-accounts",
  AdminServiceAccounts: "/admin/service-accounts",
  AdminDataLifecycle: "/admin/data-lifecycle",
  AdminAudit: "/admin/audit",
};

interface NavigationContextType {
  modelId: string | null;
  loading: boolean;
  clearModelId: () => void;
  currentTeamId: string | null;
  selectedPage: string;
  navigateToPage: (page: string) => void;
  navigateToTeam: (teamId: string) => void;
  refreshTeams: () => void;
  teamsUpdateTrigger: number;
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined,
);

export const NavigationProvider = ({ children }) => {
  const [modelId, setModelId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { model_id, team_id } = useParams();
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null);
  const [teamsUpdateTrigger, setTeamsUpdateTrigger] = useState(0);

  const selectedPage = useMemo((): string => {
    if (PAGE_PATH[location.pathname]) {
      return PAGE_PATH[location.pathname];
    }
    if (team_id) {
      return `teams/${team_id}`;
    }
    // Settings pages (a space's, an org's) belong to no rail item — falling
    // back to "Overview" would highlight a page the user is not on.
    if (
      location.pathname.startsWith("/spaces/") ||
      location.pathname.startsWith("/organizations/")
    ) {
      return "Settings";
    }
    return "Overview";
  }, [location.pathname, team_id]);

  const clearModelId = useCallback(() => {
    sessionStorage.removeItem("model_id");
    setModelId(null);
  }, []);

  const navigateToPage = useCallback(
    (page: string) => {
      if (selectedPage === page) {
        return;
      }

      const targetRoute = PAGE_ROUTES[page] || `/${page.toLowerCase()}`;
      navigate(targetRoute);
    },
    [selectedPage, navigate],
  );

  const navigateToTeam = useCallback(
    (teamId: string) => {
      localStorage.setItem("teamID", teamId);
      navigate(`/teams/${teamId}`);
    },
    [navigate],
  );

  const refreshTeams = useCallback(() => {
    setTeamsUpdateTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    try {
      setCurrentTeamId(team_id || null);

      const storedModelId = sessionStorage.getItem("model_id");
      if (model_id) {
        setModelId(model_id);
        sessionStorage.setItem("model_id", model_id);
      } else if (location.pathname.startsWith("/canvas") && storedModelId) {
        setModelId(storedModelId);
      } else {
        setModelId(null);
      }
    } catch (error) {
      log.error("Error while checking canvas session", error);
    } finally {
      setLoading(false);
    }
  }, [location.pathname, model_id, team_id]);

  return (
    <NavigationContext.Provider
      value={{
        modelId,
        clearModelId,
        currentTeamId,
        selectedPage,
        navigateToPage,
        navigateToTeam,
        refreshTeams,
        loading,
        teamsUpdateTrigger,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
};
